export const unauthorizedError = '401-UNAUTHORIZED';

// Class for managing ajax calls to/from a service.  Will insert authentication headers
// as needed, as well as parse out any refresh tokens that are passed back in
// the response
class PayScaleAgent {
    private readonly _tokenGetter: () => string | null;
    private readonly _tokenSetter: (token: string) => void;
    constructor(getToken: () => string | null, setToken: (token: string) => void) {
        this._tokenGetter = getToken;
        this._tokenSetter = setToken;
    }

    /*
        GETs a URL and then executes the call back it's given
        Returns: a Promise that resolves when the response is received and the call back is done executing
        url: The absolute or relative URL to GET. Will include XSRF token
    */
    async get<T>(url: string): Promise<T> {
        const init: RequestInit = {
            method: 'GET',
            headers: this._getHeaders(),
            mode: 'cors',
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        };

        const response = await  window.fetch(url, init);
        await this._refreshToken(response);
        return response.status === 204 ? null : await response.json();
    }

    /*
        POST data to a URL and execute the passed callback
        Returns: a Promise that resolves when the response is received and the call back is done executing
        url: The endpoint to POST to
        bodyData: the object to pass in the body of the request
    */
    async post<T>(url: string, bodyData: Object): Promise<T>  {
        const init: RequestInit = {
            method: 'POST',
            headers: this._getHeaders(),
            body: JSON.stringify(bodyData),
            mode: 'cors',
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        };
        const response = await  window.fetch(url, init);
        await this._refreshToken(response);
        return response.status === 204 ? null : await response.json();
    }

    /*
        DELETE data to a URL and execute the passed callback
        Returns: a Promise that resolves when the response is received and the call back is done executing
        url: The endpoint to DELETE to
        bodyData: the object to pass in the body of the request
    */
   async delete<T>(url: string, bodyData: Object): Promise<T>  {
    const init: RequestInit = {
        method: 'DELETE',
        headers: this._getHeaders(),
        body: JSON.stringify(bodyData),
        mode: 'cors',
        // This needs to be no store or else IE11 caches the result of everything
        cache: 'no-store'
    };
    const response = await  window.fetch(url, init);
    await this._refreshToken(response);
    return response.status === 204 ? null : await response.json();
}

    /*
        PUT data to a URL and execute the passed callback
        Returns: a Promise that resolves when the response is received and the call back is done executing
        url: The endpoint to PUT to
        bodyData: the object to pass in the body of the request
    */
   async put<T>(url: string, bodyData: Object): Promise<T>  {
    const init: RequestInit = {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify(bodyData),
        mode: 'cors',
        // This needs to be no store or else IE11 caches the result of everything
        cache: 'no-store'
    };
    const response = await  window.fetch(url, init);
    await this._refreshToken(response);
    return response.status === 204 ? null : await response.json();
}

    // Build up headers for request, inserting any bearer token
    // that is provided by the token getter provided in constructor
    private _getHeaders(): Headers {
        const headerProps: HeadersInit = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            // Adding in these so that IE knows not cache f'n AJAX
            'pragma': 'no-cache',
            'cache-control': 'no-cache'
        };

        // add these conditionally as setting 'undefined' or 'null' actually
        // gets serialized and sent to the server
        const token = this._tokenGetter ? this._tokenGetter() : null;
        if (token) {
            headerProps.Authorization = `Bearer ${token}`;
        }

        return new Headers(headerProps);
    }

    // Inspect response from get/post calls and check for either a
    // new token (to pass to our token setter), or check for token errors
    // and throw them specially
    private async _refreshToken(response: Response): Promise<void> {
        if (response.ok && !!response.headers) {
            const jwtHeader = response.headers.get('jwt');
            if (jwtHeader && this._tokenSetter) {
                this._tokenSetter(jwtHeader);
            }
        } else if (!response.ok) {
            if (response.status === 401) {
                throw new Error(unauthorizedError);
            } else {
                throw new Error(response.statusText);
            }
        }
    }
}

export { PayScaleAgent };
export default PayScaleAgent;


// WEBPACK FOOTER //
// ./src/utils/payscaleagent.ts